import { useAccess } from 'providers/access';
import React, { useCallback, useEffect, useState } from 'react';
import { getApps } from 'resources/api';
import IApp, { ScreenApp, SummaryApp } from '@appsinti/common-utils/interfaces/IApp';
import { resolveGridSize } from 'pages/home/resolveGridSize';

export type AppContextType = {
    apps: {[key: number | string]: IApp[] } | null;
    gridApps: ScreenApp[];
    summaryApp: SummaryApp | null;
    selectGroup: (groupdId: string | null) => void
};

const AppContext = React.createContext<AppContextType>({
    apps: null,
    gridApps: [],
    summaryApp: null,
    selectGroup: () => {}
});

const moduleSizeValue: { [key: string]: number } = {
    eight: 0,
    quarter: 1,
    half: 2,
  };  

export const useApps = () => React.useContext(AppContext);

export const AppProvider: React.FC<{ children: any }> = ({ children }) => {
    const [apps, setApps] = useState<{[key: number | string]: IApp[] } | null>(null);
    const { groupsIds } = useAccess();
    const [summaryApp, setSummaryApp] = useState<SummaryApp | null>(null);
    const [gridApps, setGridApps] = useState<ScreenApp[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

    useEffect(()=>{
        if(groupsIds.length === 0) return;
        getAppsFromIds(groupsIds);
    }, [groupsIds]);

    const getAppsFromIds = async (groupsIds: number[]) => {
        const allApps: {[key: number | string]: IApp[] } = {};
        for(const id of groupsIds) {
            const apps = await getApps(id);
            if(apps.length > 0) {
                allApps[+id] = apps;
            }
        }

        let summaryApp: SummaryApp | null = null;
        if(groupsIds.length > 1) {
            const apps = await getApps(null);
            for(const app of apps) {
                if(app.blueprint.type === "summary") {
                    summaryApp = app as SummaryApp;
                    break;
                }
            }
        }
        setSummaryApp(summaryApp);

        setApps(allApps);
    }

    const selectGroup = useCallback((groupdId: string | null) => {
        setSelectedGroup(groupdId);
    }, []);

    useEffect(()=>{
        if(apps === null) return;
        if(!selectedGroup) { 
            setGridApps([]);
            return;
        }

        const selectedApps = apps[selectedGroup];
        if(!selectedApps) return;

        const gridApps: ScreenApp[] = [];
        for(const app of selectedApps) {
            if(app.blueprint.type === "screen") {
                gridApps.push(app as ScreenApp);
            }
        }

        setGridApps(gridApps.sort((a, b) => {
              const aSize = resolveGridSize(a);
              const bSize = resolveGridSize(b);

              if (aSize && bSize) {
                const aValue = moduleSizeValue[aSize];
                const bValue = moduleSizeValue[bSize];
                if (aValue !== bValue) return aValue - bValue;
                // Camera widgets lead the grid within the same size tier.
                if (a.module === 'bigbrother') return -1;
                if (b.module === 'bigbrother') return 1;
                return 0;
              }

              return 0;
            }));
    }, [selectedGroup, apps]);

    return <AppContext.Provider value={{ apps, selectGroup, gridApps, summaryApp }}>{children}</AppContext.Provider>;
};
